import { WallLateralDatumAnchorAll } from "building-types/datums/datumsTypes";
import { WallHorizontalDatum } from "building-types/datums/datumsTypesEnum";
import type {
  HorizontalEdgeParamNoExpr,
  HorizontalEdgePlacementMatchExpr,
  HorizontalEdgePlacementNoExpr,
  LateralEdgePlacement,
} from "building-types/entities/edgePlacementTypes";
import type { WallLayerType } from "building-types/walls/wallEnums";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { DIST_EPSILON } from "geom/core";
import type { LineAnchor } from "geom/polyLine2Types";
import type { PolyLine2 } from "geom/polyLine2Types";
import { roomWidthAtParamFromOutline } from "../../rooms/roomOutlineIntersection";
import { wallLayerOutlineFromLateralDatum } from "../../rooms/roomOutlineUtils";
import type { EdgeLayerTable } from "./openingWallLayersTypes";

/**
 * Maps a param from a specific room edge layer with different extents to a param on the room edge.
 */
export function entityParamFromSpecificWallLayer({
  placement,
  largestOffset,
  edgeLength,
  mirror,
  mostInwardLayerIntoRoom,
  edgeMinTable,
  edgeMaxTable,
}: {
  placement: HorizontalEdgePlacementNoExpr;
  largestOffset: number | undefined;
  edgeLength: number;
  mirror: boolean;
  mostInwardLayerIntoRoom: WallLayerType | undefined;
  edgeMinTable: EdgeLayerTable;
  edgeMaxTable: EdgeLayerTable;
}): { param: number; layerLength: number; layerMin: number; layerMax: number } {
  let param: number;

  if (
    placement.horizontalDatum === WallHorizontalDatum.ROOM_EDGE ||
    (placement.horizontalDatum === WallHorizontalDatum.OUTSIDE_OF_SURFACE && largestOffset === undefined)
  ) {
    param = entityParamDenormalize(placement, edgeLength, mirror);
    return { param, layerLength: edgeLength, layerMin: 0, layerMax: edgeLength };
  }

  const layer =
    placement.horizontalDatum === WallHorizontalDatum.OUTSIDE_OF_SURFACE
      ? mostInwardLayerIntoRoom
      : placement.horizontalDatum;

  if (layer === undefined || edgeMinTable[layer] === undefined) {
    param = entityParamDenormalize(placement, edgeLength, mirror);
    return { param, layerLength: edgeLength, layerMin: 0, layerMax: edgeLength };
  }

  const layerMin = edgeMinTable[layer];
  const layerMax = edgeMaxTable[layer];

  assertIsNotUndefined(layerMin);
  assertIsNotUndefined(layerMax);
  const layerLength = layerMax - layerMin;

  param = entityParamDenormalize(placement, layerLength, mirror) + layerMin;
  return { param, layerLength, layerMin, layerMax };
}

export function entityDomainNormalize(
  placement: Pick<HorizontalEdgePlacementMatchExpr, "paramAnchor">,
  domain: number[],
  mirror: boolean,
): number[] {
  const { paramAnchor } = placement;
  let [start, end] = domain;

  if (mirror) {
    [start, end] = [end, start];
  }

  if (isEdgeHostedEntityFromCenter(placement)) {
    const half = Math.abs(end - start);
    end = start + half;
    start = start - half;
  } else if (paramAnchor === "END") {
    [start, end] = [end, start];
  }

  return [start, end];
}

export function entityDomainDenormalize(
  placement: { paramAnchor: LineAnchor | undefined },
  domain: number[],
  mirror: boolean,
): number[] {
  const { paramAnchor = "START" } = placement;
  let [start, end] = domain;

  if (mirror) {
    [start, end] = [end, start];
  }

  if (isEdgeHostedEntityFromCenter(placement)) {
    const len = Math.abs(end - start);
    end = start;
    const mid = len / 2;
    start = start + mid * (mirror ? -1 : 1);
  } else if (paramAnchor === "END") {
    [start, end] = [end, start];
  }

  return [start, end];
}

export function entityParamNormalize(
  placement: { param: number | undefined; paramAnchor: LineAnchor | undefined; paramNormalized: boolean | undefined },
  domainLength: number,
  mirror: boolean,
): number {
  const { paramAnchor = "START", param = 0, paramNormalized = false } = placement;
  let value;
  if (paramAnchor === "START") {
    value = mirror ? domainLength - param : param;
  }
  if (paramAnchor === "END") {
    value = mirror ? param : domainLength - param;
  }
  if (paramAnchor === "CENTER") {
    domainLength /= 2;
    value = mirror ? param - domainLength : (value = domainLength - param);
  }
  if (domainLength === 0) {
    domainLength = 1;
  }
  return paramNormalized ? value / domainLength : value;
}

interface HorizontalEdgePlacementWithParamAnchor extends HorizontalEdgeParamNoExpr {
  readonly paramAnchor: LineAnchor;
}

/**
 * Adjusts a room edge param to account for mirroring and normalization if necessary.
 * @param placement
 * @param domainLength
 * @param mirror
 * @returns {number} denormalized number relative to the domain
 */
export function entityParamDenormalize(
  placement: HorizontalEdgePlacementWithParamAnchor,
  domainLength: number,
  mirror: boolean,
) {
  const { param = 0, paramNormalized: normalized = false, paramAnchor } = placement;

  let scalarDirection = 1;

  const [ptStart, ptEnd] = entityDomainDenormalize({ paramAnchor }, [0, domainLength], mirror);
  const domainLengthFinal = Math.abs(ptEnd - ptStart);

  const vecParam = normalized ? param : param / (domainLengthFinal === 0 ? 1 : domainLengthFinal);

  if (isEdgeHostedEntityFromEnd(placement)) {
    scalarDirection = -1;
  }

  if (mirror) {
    scalarDirection = -scalarDirection;
  }

  return ptStart + domainLengthFinal * vecParam * scalarDirection;
}

/**
 * Shifts a param on a room edge to account for mirroring and normalization if necessary.
 */
export function entityParamShift(
  placement: HorizontalEdgePlacementNoExpr,
  domainLength: number,
  mirror: boolean,
  shift: number,
): number {
  if (placement.paramNormalized) {
    if (Math.abs(domainLength) < DIST_EPSILON) {
      return placement.param;
    }
    shift /= domainLength;
  }

  let invert = isEdgeHostedEntityFromEnd(placement);

  if (mirror) {
    invert = !invert;
  }

  return invert ? placement.param - shift : placement.param + shift;
}

export function entityOffsetShift(
  placement: Pick<LateralEdgePlacement, "lateralOffset" | "lateralOffsetNormalized">,
  roomWidth: number,
  shift: number,
): number {
  if (placement.lateralOffsetNormalized) {
    if (Math.abs(roomWidth) < DIST_EPSILON) {
      return placement.lateralOffset;
    }
    shift /= roomWidth;
  }

  return (placement.lateralOffset ?? 0) - shift;
}

/**
 * Since we only have the doc and not building, we can only get the room outline without wall thickness.
 * @param resolvedParam expects denormalized param
 */
export function docEdgeHostedEntityGetRoomWidthAtParam(
  room: { outline: PolyLine2 },
  edgeIndex: number,
  resolvedParam: number,
): number {
  const wallLayerOutline = wallLayerOutlineFromLateralDatum(room, undefined, WallLateralDatumAnchorAll.ROOM_EDGE);
  return roomWidthAtParamFromOutline(resolvedParam, edgeIndex, room, wallLayerOutline, 0);
}

export function isEdgeHostedEntityFromCenter(placement: { paramAnchor?: LineAnchor }): boolean {
  return placement.paramAnchor === "CENTER";
}

export function isEdgeHostedEntityFromEnd(placement: { paramAnchor?: LineAnchor }): boolean {
  return placement.paramAnchor === "END" || placement.paramAnchor === "CENTER";
}

export function isEdgeHostedEntityFromStart(placement: { paramAnchor?: LineAnchor }): boolean {
  return placement.paramAnchor === "START";
}
